import request from '@/utils/request'

const getAuthHeaders = ({ token }) => ({
  'x-tamara-token': token,
})

const getAuthMetaData = (params) => ({
  options: {
    headers: getAuthHeaders(params),
  },
})

export function login({
  email,
  password,
  phoneNumber,
  countryCode,
  deviceIdentifier,
  reCaptchaToken,
  utmParams,
}) {
  return request(
    {
      path: '/merchants/login',
      method: 'POST',
      data: {
        email,
        password,
        phone_number: phoneNumber,
        country_code: countryCode,
        device_identifier: deviceIdentifier,
        utm_source: utmParams?.utm_source || '',
      },
      messageDuration: 8000,
    },
    getAuthMetaData({ token: reCaptchaToken })
  )
}

// This function will be maintained by the Merchant Onboarding squad
export function signup(dataToBeSent) {
  return request(
    {
      path: '/v2/onboarding/initialize',
      method: 'POST',
      data: {
        email: dataToBeSent.email,
        identifier: dataToBeSent.identifier,
        locale: dataToBeSent.locale,
        password: dataToBeSent.password,
        password_confirmation: dataToBeSent.passwordConfirmation,
      },
    },
    { options: { ignoreMerchantId: true, attachReCaptchaToken: true } }
  )
}

export function verifyAccount(dataToBeSent) {
  return request(
    {
      path: '/v2/onboarding/otp-verification',
      method: 'POST',
      data: {
        email: dataToBeSent.email,
        identifier: dataToBeSent.identifier,
        locale: dataToBeSent.locale,
        password: dataToBeSent.password,
        password_confirmation: dataToBeSent.passwordConfirmation,
        verification_code: dataToBeSent.verificationCode,
        is_mobile: false,
      },
    },
    {
      options: {
        ignoreMerchantId: true,
        attachReCaptchaToken: 'verifyAccountOnboarding',
        hideMessage: true,
      },
    }
  )
}

export function resendSignupVerificationCode(dataToBeSent) {
  return request(
    {
      path: '/v2/onboarding/resend-otp',
      method: 'POST',
      data: {
        email: dataToBeSent.email,
        identifier: dataToBeSent.identifier,
        locale: dataToBeSent.locale,
      },
    },
    {
      options: {
        ignoreMerchantId: true,
        attachReCaptchaToken: 'resendSignupVerificationCodeOnboarding',
      },
    }
  )
}

export function fetchSsoSettings(provider) {
  return request({
    path: '/merchants/auth',
    method: 'GET',
    data: { auth_provider: provider },
  })
}

export function loginSso({ provider, authCode }) {
  return request({
    path: `/merchants/auth/confirm?auth_provider=${provider}&code=${authCode}`,
    method: 'POST',
  })
}

export function activate({ userId, newPassword, confirmPassword, hashCode }) {
  return request(
    {
      method: 'POST',
      path: `/merchants/users/${userId}/activate`,
      data: {
        hash_code: hashCode,
        password: newPassword,
        confirm_password: confirmPassword,
      },
    },
    {
      options: {
        hideMessage: true,
        attachToken: false,
        attachReCaptchaToken: 'activateUser',
      },
    }
  )
}

export function forgotPassword({ email, locale }) {
  return request(
    {
      path: '/merchants/users/send-reset-password-email',
      method: 'POST',
      data: { email, locale },
    },
    {
      options: {
        hideMessage: true,
        attachToken: false,
        attachReCaptchaToken: 'forgotPassword',
      },
    }
  )
}

export function resendActivateEmail({ userId, locale, reCaptchaToken }) {
  const meta = getAuthMetaData({ token: reCaptchaToken })

  meta.options = { ...meta.options, hideMessage: true }

  return request(
    {
      path: `/merchants/users/${userId}/resend-activate-email`,
      method: 'POST',
      data: { locale },
    },
    meta
  )
}

export function resetPassword({ userId, hashCode, password, confirmPassword }) {
  return request(
    {
      path: `/merchants/users/${userId}/reset-password`,
      method: 'POST',
      data: {
        hash_code: hashCode,
        password,
        confirm_password: confirmPassword,
      },
    },
    {
      options: {
        hideMessage: true,
        attachToken: false,
        attachReCaptchaToken: 'resetPassword',
      },
    }
  )
}

export function fetchUsers(data) {
  return request({
    path: '/merchants/users',
    method: 'GET',
    data,
  })
}

export function fetchUserDetail(userId) {
  return request({
    path: `/merchants/users/${userId}`,
    method: 'GET',
  })
}

export function createUser(data) {
  return request(
    {
      path: `/merchants/users/create`,
      method: 'POST',
      data: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        role: data.role,
        locale: data.locale,
        stores: data.stores,
        branches: data.branches,
        time_zone: data.timeZone,
      },
    },
    {
      options: { hideMessage: true },
    }
  )
}

export function updateUser(data) {
  return request(
    {
      path: `/merchants/users/${data.userId}/update`,
      method: 'PUT',
      data: {
        first_name: data.firstName,
        last_name: data.lastName,
        role: data.role,
        locale: data.locale,
        stores: data.stores,
        branches: data.branches,
        time_zone: data.timeZone,
      },
    },
    {
      options: { hideMessage: true },
    }
  )
}

export function deleteUser({ userId, stores }) {
  return request(
    {
      path: `/merchants/users/${userId}/delete`,
      method: 'DELETE',
      data: {
        stores,
      },
    },
    {
      options: { hideMessage: true },
    }
  )
}

export function fetchRoles() {
  return request({
    path: '/users/roles/all',
    method: 'GET',
  })
}

export function fetchPermissionGroups() {
  return request({
    path: '/permissions/all',
    method: 'GET',
  })
}

export function fetchRoleDetail(roleId) {
  return request({
    path: `/users/roles/${roleId}/details`,
    method: 'GET',
  })
}

export function createRole(data) {
  return request(
    {
      path: `/users/roles/create`,
      method: 'POST',
      data: {
        role_name: data.roleName,
        role_name_ar: data.roleNameAr,
        role_description: data.roleDescription,
        role_description_ar: data.roleDescriptionAr,
        permissions: data.permissions,
      },
    },
    {
      options: { hideMessage: true },
    }
  )
}

export function updateRole(data) {
  return request(
    {
      path: `/users/roles/${data.roleId}/update`,
      method: 'PUT',
      data: {
        role_name: data.roleName,
        role_name_ar: data.roleNameAr,
        role_description: data.roleDescription,
        role_description_ar: data.roleDescriptionAr,
        permissions: data.permissions,
      },
    },
    {
      options: { hideMessage: true },
    }
  )
}

export function fetchUserBranches(params) {
  return request({
    path: `/merchants/users/branches?${params}`,
    method: 'GET',
  })
}

export function downloadUsers(data) {
  return request(
    {
      timeout: 100000, // ms
      method: 'GET',
      path: `/merchants/users/report`,
      data: data,
    },
    {
      options: {
        hideMessage: true,
        camelizeRes: false,
      },
    }
  )
}
